import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Pagination from "../../components/Tables/Pagination";
import InputSearch from "../../components/inputs/InputSearch";
import SelectWithIcon from "../../components/inputs/SelectWithIcon";
import AdminLearningPathCard from "../../components/cards/AdminLearningPathCard";
import AddEmployeeToLearningTrackModal from "../../components/Modals/AddEmployeeToLearningTrackModal";
import { useLazyListAdminLearningTracksQuery } from "../../services/organization/adminLearningTrack";
import LoadingIndicator from "../../components/LoadingIndicator";
import EmptyState from "../../components/EmptyState";
import Icon from "../../components/icons/Icon";
import { LearningPathsStrokeIcon } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";

export default function LearningPaths() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page")) || 1;
  const [page, setPage] = useState(urlPage);
  const [modalTrack, setModalTrack] = useState();
  const [learningTracksTitleFilter, setLearningTracksTitleFilter] =
    useState("");
  const [sortKeyword, setSortKeyword] = useState("-created_at");
  const [filters, setFilters] = useState(["sort=-created_at"]);

  const { t } = useTranslation(["common", "learningPaths"]);
  const [
    listTracks,
    { data: tracksList, isLoading: isListLearningTracksLoading },
  ] = useLazyListAdminLearningTracksQuery();

  useEffect(() => {
    const currentPage = parseInt(searchParams.get("page")) || 1;
    if (page !== currentPage) {
      setSearchParams({ page: page.toString() }, { replace: true });
    }
  }, [page, setSearchParams]);

  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page")) || 1;
    if (urlPage !== page) {
      setPage(urlPage);
    }
  }, [searchParams]);

  useEffect(() => {
    listTracks({
      filters: filters.join("&"),
      page,
    });
  }, [page, filters, listTracks]);

  useEffect(() => {
    let result = [];
    if (learningTracksTitleFilter?.length > 1) {
      result.push(`filters[keyword]=${learningTracksTitleFilter.trim()}`);
    }
    if (sortKeyword) result.push(`sort=${sortKeyword}`);
    setFilters(result);
  }, [learningTracksTitleFilter, sortKeyword]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  function updateLearningTracksTitleFilter(e) {
    setLearningTracksTitleFilter(e.target.value.trim());
    setPage(1);
  }

  function isSearching() {
    return learningTracksTitleFilter?.length > 1;
  }

  if (isListLearningTracksLoading) return <LoadingIndicator />;

  return (
    <div className="employee learning-tracks-page">
      <header className="learning-tracks-page__toolbar">
        <div className="learning-tracks-page__intro">
          <h1 className="title-page mb-0">
            {t("learningPaths:learningTracks")}
          </h1>
          <ul className="learning-tracks-page__stats">
            <li>
              <span className="label label-black">
                <span className="me-2">{tracksList?.pagination?.count}</span>
                {t("learningPaths:learningTracks")}
              </span>
            </li>
          </ul>
        </div>

        <div className="learning-tracks-page__actions">
          <div className="learning-tracks-page__search">
            <InputSearch
              placeHolder={t("learningPaths:searchLearningPath")}
              onChange={updateLearningTracksTitleFilter}
            />
          </div>
          <SelectWithIcon
            value={sortKeyword}
            onChange={(e) => {
              setPage(1);
              setSortKeyword(e.target.value);
            }}
          >
            <option defaultValue value="">
              {t("sorting.sortBy")}
            </option>
            <option value="-created_at">{t("sorting.newFirst")}</option>
            <option value="created_at">{t("sorting.oldFirst")}</option>
          </SelectWithIcon>
          <Link to="/admin/learning_paths/new">
            <button type="button" className="btn btn-main-color font-15">
              <span className="fas fa-plus fa-fw me-2"></span>
              {t("learningPaths:addNewPath")}
            </button>
          </Link>
        </div>
      </header>

      <div className="learning-tracks-page__body">
        {tracksList?.data.length > 0 ? (
          <ul className="learning-tracks-page__list">
            {tracksList?.data?.map((track) => (
              <li key={`track-${track.id}`}>
                <AdminLearningPathCard
                  setModalTrack={setModalTrack}
                  track={track}
                  modalId="add-employee-learning-path"
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className="learning-tracks-page__empty">
            <EmptyState
              iconComponent={
                <Icon
                  icon={<LearningPathsStrokeIcon />}
                  hasStroke
                  iconName="learning-path-stroke"
                  iconColor="#bbbabf"
                  iconSize={38}
                  viewBox="0 0 23 19"
                />
              }
              title={t("emptyStates.learningPaths")}
              customTitle={
                isSearching() ? t("learningPaths:noTracksFound") : null
              }
              emptyStateText={t("learningPaths:addTracks")}
            />
          </div>
        )}

        {tracksList?.data?.length ? (
          <Pagination
            current={tracksList?.pagination?.current}
            pages={tracksList?.pagination?.pages}
            handlePagination={(page) => setPage(page)}
          />
        ) : null}
      </div>

      <AddEmployeeToLearningTrackModal
        id="add-employee-learning-path"
        learningTrack={modalTrack}
        isOpened={modalTrack != null}
        onClose={() => setModalTrack(null)}
      />
    </div>
  );
}
